import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { spotlight, secondaryAnnouncements } from '@/data/spotlight';
import type { SpotlightData, SecondaryAnnouncementData, SpotlightType } from '@/data/spotlight';

type SlideItem =
  | ({ slideKind: 'main' } & SpotlightData)
  | ({ slideKind: 'secondary' } & SecondaryAnnouncementData);

const TYPE_CONFIG: Record<SpotlightType, {
  icon: string;
  badgeBg: string;
  badgeText: string;
  iconColor: string;
  accentColor: string;
  borderColor: string;
  bgColor: string;
  buttonBg: string;
  buttonText: string;
  cornerAccent: string;
  timerBg: string;
  timerText: string;
  timerBorder: string;
}> = {
  feast: {
    icon: 'ri-calendar-check-line',
    badgeBg: 'bg-amber-50/90 border-amber-300',
    badgeText: 'text-amber-800',
    iconColor: 'text-amber-500',
    accentColor: 'text-amber-600',
    borderColor: 'border-amber-200',
    bgColor: 'bg-amber-50',
    buttonBg: 'bg-amber-500 hover:bg-amber-600',
    buttonText: 'text-white',
    cornerAccent: 'bg-amber-400',
    timerBg: 'bg-amber-500',
    timerText: 'text-white',
    timerBorder: 'border-amber-400',
  },
  fast: {
    icon: 'ri-timer-line',
    badgeBg: 'bg-sky-50/90 border-sky-300',
    badgeText: 'text-sky-800',
    iconColor: 'text-sky-500',
    accentColor: 'text-sky-600',
    borderColor: 'border-sky-200',
    bgColor: 'bg-sky-50',
    buttonBg: 'bg-sky-600 hover:bg-sky-700',
    buttonText: 'text-white',
    cornerAccent: 'bg-sky-400',
    timerBg: 'bg-sky-500',
    timerText: 'text-white',
    timerBorder: 'border-sky-400',
  },
  'holy-week': {
    icon: 'ri-cross-line',
    badgeBg: 'bg-rose-50/90 border-rose-300',
    badgeText: 'text-rose-800',
    iconColor: 'text-rose-500',
    accentColor: 'text-rose-600',
    borderColor: 'border-rose-200',
    bgColor: 'bg-rose-50',
    buttonBg: 'bg-rose-700 hover:bg-rose-800',
    buttonText: 'text-white',
    cornerAccent: 'bg-rose-500',
    timerBg: 'bg-rose-600',
    timerText: 'text-white',
    timerBorder: 'border-rose-400',
  },
  event: {
    icon: 'ri-calendar-event-line',
    badgeBg: 'bg-emerald-50/90 border-emerald-300',
    badgeText: 'text-emerald-800',
    iconColor: 'text-emerald-500',
    accentColor: 'text-emerald-600',
    borderColor: 'border-emerald-200',
    bgColor: 'bg-emerald-50',
    buttonBg: 'bg-emerald-600 hover:bg-emerald-700',
    buttonText: 'text-white',
    cornerAccent: 'bg-emerald-400',
    timerBg: 'bg-emerald-500',
    timerText: 'text-white',
    timerBorder: 'border-emerald-400',
  },
  choir: {
    icon: 'ri-music-2-line',
    badgeBg: 'bg-rose-50/90 border-rose-300',
    badgeText: 'text-rose-800',
    iconColor: 'text-rose-500',
    accentColor: 'text-rose-600',
    borderColor: 'border-rose-200',
    bgColor: 'bg-rose-50',
    buttonBg: 'bg-rose-600 hover:bg-rose-700',
    buttonText: 'text-white',
    cornerAccent: 'bg-rose-400',
    timerBg: 'bg-rose-500',
    timerText: 'text-white',
    timerBorder: 'border-rose-400',
  },
  announcement: {
    icon: 'ri-megaphone-line',
    badgeBg: 'bg-stone-100/90 border-stone-300',
    badgeText: 'text-stone-700',
    iconColor: 'text-stone-500',
    accentColor: 'text-stone-600',
    borderColor: 'border-stone-300',
    bgColor: 'bg-stone-50',
    buttonBg: 'bg-stone-700 hover:bg-stone-800',
    buttonText: 'text-white',
    cornerAccent: 'bg-stone-400',
    timerBg: 'bg-stone-600',
    timerText: 'text-white',
    timerBorder: 'border-stone-400',
  },
};



interface ArticleCardProps {
  slide: SlideItem;
  index: number;
}

function ArticleCard({ slide, index }: ArticleCardProps) {
  const cfg = TYPE_CONFIG[slide.type];
  const isMain = slide.slideKind === 'main';
  const subtitle = isMain ? (slide as SpotlightData).subtitle : undefined;
  const dateRange = slide.dateRange;
  const time = !isMain ? (slide as SecondaryAnnouncementData).time : undefined;
  const ctaLabel = slide.ctaLabel;
  const ctaLink = slide.ctaLink;

  return (
    <div
      className="flex flex-col bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-stone-200 transition-all duration-300 group"
      style={{ animationDelay: `${index * 120}ms` }}
    >
      {/* Image */}
      <div className="relative">
        <div className="h-52 sm:h-56 overflow-hidden">
          <img
            src={slide.image}
            alt={slide.imageAlt}
            className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
        </div>

        {/* Type badge — top-right of image */}
        <div className="absolute top-3 right-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest backdrop-blur-sm ${cfg.badgeBg} ${cfg.badgeText}`}>
            <i className={`${cfg.icon} ${cfg.iconColor} text-xs`}></i>
            {slide.type.replace('-', ' ')}
          </span>
        </div>

        {/* Date row — bottom of image */}
        {(dateRange || time) && (
          <div className="absolute bottom-3 left-3 right-3 flex items-center gap-2">
            {dateRange && (
              <span className="inline-flex items-center gap-1 text-white/90 text-[11px] font-medium">
                <i className="ri-calendar-line text-xs"></i>
                {dateRange}
              </span>
            )}
            {dateRange && time && <span className="text-white/40 text-xs">·</span>}
            {time && (
              <span className="inline-flex items-center gap-1 text-white/90 text-[11px] font-medium">
                <i className="ri-time-line text-xs"></i>
                {time}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Text body */}
      <div className="flex flex-col flex-1 px-5 py-5">
        {/* Subtitle */}
        {subtitle && (
          <p
            className={`font-serif italic text-sm mb-1.5 ${cfg.accentColor}`}
            style={{ fontFamily: '"Crimson Text", serif' }}
          >
            {subtitle}
          </p>
        )}

        {/* Title */}
        <h3
          className="font-serif font-bold text-stone-800 text-lg sm:text-xl leading-snug mb-3"
          style={{ fontFamily: '"Crimson Text", serif' }}
        >
          {slide.title}
        </h3>

        {/* Thin accent line */}
        <div className={`h-0.5 w-8 rounded-full ${cfg.cornerAccent} opacity-40 mb-3`}></div>

        {/* Description */}
        <p className="text-stone-500 text-xs sm:text-sm leading-relaxed flex-1 line-clamp-4">
          {slide.description}
        </p>

        {/* CTA — always show a news link */}
        <div className="mt-4 pt-4 border-t border-stone-100">
          <Link
            to={ctaLink || '/news'}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap cursor-pointer w-full justify-center ${cfg.buttonBg} ${cfg.buttonText}`}
          >
            <i className="ri-newspaper-line text-xs"></i>
            {ctaLabel || 'Read More on News'}
            <i className="ri-arrow-right-line text-xs"></i>
          </Link>
        </div>
      </div>
    </div>
  );
}

export default function FeaturedSpotlight() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const activeSecondary = secondaryAnnouncements.filter(a => a.active);

  const slides: SlideItem[] = [
    ...(spotlight.active ? [{ slideKind: 'main' as const, ...spotlight }] : []),
    ...activeSecondary.map(s => ({ slideKind: 'secondary' as const, ...s })),
  ];

  if (slides.length === 0) return null;

  // Pair up slides into rows of 2
  const rows: SlideItem[][] = [];
  for (let i = 0; i < slides.length; i += 2) {
    rows.push(slides.slice(i, i + 2));
  }

  return (
    <div className={`transition-opacity duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="space-y-6">
        {rows.map((row, rowIdx) => (
          <div key={rowIdx} className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {row.map((slide, colIdx) => (
              <ArticleCard key={colIdx} slide={slide} index={rowIdx * 2 + colIdx} />
            ))}
            {/* If odd number of slides, fill the second column with a subtle placeholder */}
            {row.length === 1 && (
              <div className="hidden sm:flex items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50/50 min-h-[300px]">
                <p className="text-stone-300 text-xs font-medium tracking-wide uppercase">More coming soon</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
